import { Check, ImageIcon } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MARKETING_SLIDES, SLIDE_DURATION_MS } from './marketingSlides';
import {
  MediaLabel,
  MediaPlaceholder,
  Rail,
  RailBar,
  RailBarFill,
  RailButton,
  Slide,
  SlideEyebrow,
  SlidePoint,
  SlidePoints,
  Slideshow,
  SlideText,
  SlideTitle,
  Track,
  Viewport,
} from './MarketingSlideshow.styled';

/**
 * TYMCZASOWO wyłączone ramki na zrzuty ekranu — do czasu, aż powstaną grafiki.
 * Zmiana na `true` przywraca placeholdery bez żadnych innych poprawek; przy okazji
 * wraca wyższy `Viewport`, bo ramka zajmowała dolną część slajdu.
 */
const SHOW_MEDIA_PLACEHOLDER = false;

/** Przy wyłączonych animacjach pokaz stoi na pierwszym slajdzie, a listwa działa ręcznie. */
const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Pionowy pokaz slajdów na ekranie logowania.
 *
 * Slajdy przesuwają się w pionie, a listwa po PRAWEJ stronie pokazuje kreski — aktywna
 * jest dłuższa i wypełnia się paskiem odliczającym czas do zmiany. Numery slajdów zostały
 * usunięte z widoku, ale numer nadal jest w `aria-label` przycisku: bez niego czytnik
 * ekranu nie miałby czym rozróżnić czterech identycznych kresek.
 * Zatrzymanie kursora nad pokazem wstrzymuje odliczanie: kto czyta dłużej, nie traci
 * treści w połowie zdania.
 */
export const MarketingSlideshow: FC = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused || prefersReducedMotion()) return undefined;
    const timer = window.setTimeout(
      () => setActiveIndex((current) => (current + 1) % MARKETING_SLIDES.length),
      SLIDE_DURATION_MS,
    );
    return () => window.clearTimeout(timer);
  }, [activeIndex, isPaused]);

  return (
    <Slideshow
      aria-roledescription="carousel"
      aria-label={t('auth.signIn.slides.regionLabel', 'Możliwości panelu hotelspot')}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <Viewport $compact={!SHOW_MEDIA_PLACEHOLDER}>
        <Track $index={activeIndex}>
          {MARKETING_SLIDES.map((slide, index) => {
            const SlideIcon = slide.icon;
            const isActive = index === activeIndex;

            return (
              <Slide key={slide.id} $active={isActive} aria-hidden={!isActive}>
                <SlideEyebrow>
                  <SlideIcon />
                  {t(slide.eyebrowKey)}
                </SlideEyebrow>
                <SlideTitle>{t(slide.titleKey)}</SlideTitle>
                <SlideText>{t(slide.descriptionKey)}</SlideText>

                <SlidePoints>
                  {slide.pointKeys.map((pointKey) => (
                    <SlidePoint key={pointKey}>
                      <Check />
                      {t(pointKey)}
                    </SlidePoint>
                  ))}
                </SlidePoints>

                {/* Grafiki dojdą później; ramka trzyma dla nich miejsce i proporcje. */}
                {SHOW_MEDIA_PLACEHOLDER && (
                  <MediaPlaceholder role="img" aria-label={t(slide.mediaAltKey)}>
                    <ImageIcon />
                    <MediaLabel>
                      {t('auth.signIn.slides.mediaPlaceholder', 'Zrzut ekranu w przygotowaniu')}
                    </MediaLabel>
                  </MediaPlaceholder>
                )}
              </Slide>
            );
          })}
        </Track>
      </Viewport>

      <Rail>
        {MARKETING_SLIDES.map((slide, index) => (
          <RailButton
            key={slide.id}
            type="button"
            $active={index === activeIndex}
            aria-current={index === activeIndex}
            aria-label={t('auth.signIn.slides.goToSlide', {
              defaultValue: 'Pokaż slajd {{number}}',
              number: index + 1,
            })}
            onClick={() => setActiveIndex(index)}
          >
            <RailBar $active={index === activeIndex} $done={index < activeIndex}>
              {/* Klucz na indeksie aktywnego slajdu wymusza restart animacji paska. */}
              {index === activeIndex && <RailBarFill key={activeIndex} $paused={isPaused} />}
            </RailBar>
          </RailButton>
        ))}
      </Rail>
    </Slideshow>
  );
};
